import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FilterCriteria } from '../../../models/api/reports/filter-detail-response';
import { ControlConfig } from '../../../models/api/reports/report';
import { FiltersService } from '../../../services/filters/filters.service';
import { DisplayService } from '../../../services/reports/display.service';
import { IncrementOptionsService } from '../../../services/reports/increment-options.service';
import { ProcessedControl } from '../../../services/reports/reports.service';
import { ListboxComponent } from '../../core/controls/listbox.component';
import { SelectComponent } from '../../core/controls/select.component';
import { DatePickerFieldComponent } from '../../core/fields/date-picker-field.component';
import { FieldContainerComponent } from '../../core/fields/field-container.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BaseCriteriaComponent } from './base-criteria.component';

@Component({
  selector: 'app-report-sched-details',
  imports: [
    DatePickerFieldComponent,
    ListboxComponent,
    PageHeaderComponent,
    FieldContainerComponent,
    SelectComponent,
  ],
  template: `
    @if (display.showScheduleSetup()) {
      <app-page-header label="Report schedule" headerSize="h3" />
      <div class="tw-flex tw-flex-col tw-w-full">
        <!-- <app-select-field
          [control]="fg.controls.frequency"
          label="Frequency"
          valueClasses="tw-max-w-[300px]"
          optionLabel="name"
          optionValue="code"
          [options]="frequencyOptions()"
          [virtualScroll]="false"
          [showClear]="false"
          [filter]="false"
        /> -->

        <app-field-container
          label="Frequency"
          labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
          valueClasses="tw-basis-full sm:tw-basis-[70%]"
          [control]="fg.controls.frequency"
        >
          <ng-template #value>
            <div class="tw-flex tw-gap-2 tw-w-full">
              <app-select
                [control]="fg.controls.frequency"
                classes="tw-min-w-[200px]"
                optionLabel="name"
                optionValue="code"
                [options]="frequencyOptions()"
                [virtualScroll]="false"
                [showClear]="false"
                [filter]="false"
                screenReaderLabel="Frequency"
              />

              @if (showNoneInfoMessage()) {
                <div
                  class="tw-pl-4 xs:tw-pb-3 tw-w-full tw-text-sm tw-text-[var(--sed-black-80-color)] app-fade-in"
                >
                  Selecting None will skip scheduling this report. You can
                  schedule reports later from the Personalized Reports tab.
                </div>
              }
            </div>
          </ng-template>
        </app-field-container>

        @if (
          fg.controls.frequency.value && fg.controls.frequency.value !== 'P'
        ) {
          @if (showFirstRunNotCompletedMessage()) {
            <div
              class="tw-pl-4 tw-pb-2 tw-text-sm tw-text-[var(--sed-black-80-color)] app-fade-in"
            >
              The first recurrence of the original request has not yet
              completed, so the incrementing dates will not update if the Next
              run date is changed.
            </div>
          }
          <app-date-picker-field
            [control]="fg.controls.runDate"
            label="Next run"
            labelClasses="tw-flex tw-items-center tw-basis-full sm:tw-basis-[30%]"
            valueClasses="tw-basis-full sm:tw-basis-[70%]"
            [showTime]="true"
          />
        }
      </div>

      @if (showIncrementSections()) {
        <div class="tw-pb-4 app-fade-in">
          <div class="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 md:tw-gap-8">
            @if (scheduleOptions().length > 0) {
              <div class="tw-w-full md:tw-basis-1/3">
                <app-listbox
                  [control]="fg.controls.incDates"
                  label="Recurring schedule options"
                  optionLabel="name"
                  optionValue="displayName"
                  [options]="scheduleOptions()"
                  [filter]="false"
                ></app-listbox>
              </div>
            }
            @if (showFromToColumns()) {
              <div class="tw-w-full md:tw-basis-1/3">
                <app-listbox
                  [control]="fg.controls.incFrom"
                  label="Increment from dates"
                  optionLabel="name"
                  optionValue="displayName"
                  [options]="incrementFromOptions()"
                  [filter]="false"
                ></app-listbox>
              </div>
              <div class="tw-w-full md:tw-basis-1/3">
                <app-listbox
                  [control]="fg.controls.incTo"
                  label="Increment to dates"
                  optionLabel="name"
                  optionValue="displayName"
                  [options]="incrementToOptions()"
                  [filter]="false"
                ></app-listbox>
              </div>
            }
          </div>
        </div>
      }
    }
  `,
})
export class ReportSchedDetailsComponent
  extends BaseCriteriaComponent
  implements OnInit
{
  protected display = inject(DisplayService);
  private incrementOptions = inject(IncrementOptionsService);
  private filtersService = inject(FiltersService);

  frequencyValue = signal<string | null>('N');

  showIncrementSections = computed(() => {
    const hasRecurOptions = this.display.showRecurSchedOptions();
    const freq = this.frequencyValue();
    return hasRecurOptions && freq !== null && freq !== 'P' && freq !== 'N';
  });

  showFromToColumns = computed(() => {
    const report = this.reports.currentReport();
    return report?.reportSections?.hasIncrementFromTo ?? true;
  });

  showNoneInfoMessage = computed(() => {
    return (
      this.frequencyValue() === 'P' &&
      !this.reports.isEditScheduledDeliveryMode() &&
      !this.reports.isSchedulePersonalizedMode()
    );
  });

  showFirstRunNotCompletedMessage = computed(() => {
    const isEditScheduledMode = this.reports.isEditScheduledDeliveryMode();
    const hasRunOnce = this.reports.scheduledReportHasRunOnce();
    return isEditScheduledMode && !hasRunOnce;
  });

  frequencyOptions = computed(() => {
    const report = this.reports.currentReport();
    const runOnceOnly = report?.reportSections?.runOnceOnly ?? false;

    if (runOnceOnly) {
      return [{ name: 'Once', code: 'N' }];
    }

    const hasRecurOptions = this.display.showRecurSchedOptions();

    const suppressNone =
      this.reports.isEditScheduledDeliveryMode() ||
      this.reports.isSchedulePersonalizedMode();

    if (!hasRecurOptions) {
      return suppressNone ? [] : [{ name: 'None', code: 'P' }];
    }

    // Build full options list, conditionally including 'None'
    const options = [
      { name: 'Once', code: 'N' },
      { name: 'Daily', code: 'D' },
      { name: 'Weekly', code: 'W' },
      { name: 'Monthly', code: 'M' },
      { name: 'Quarterly', code: 'Q' },
      { name: 'Semi-Annual', code: 'S' },
      { name: 'Annual', code: 'A' },
    ];

    // Only add 'None' at the beginning if not suppressed
    if (!suppressNone) {
      options.unshift({ name: 'None', code: 'P' });
    }

    return options;
  });

  filterCriteria = signal<FilterCriteria[]>([]);

  scheduleOptions = computed(() => {
    const report = this.reports.currentReport();
    const controlsMap = report?.reportRequest?.controlsMap;
    const scheduledDateMappings = report?.reportRequest?.scheduledDateMappings;
    const requiresFilter = report?.reportSections?.requiresFilter ?? false;
    const hasFilter = requiresFilter && !!report?.reportInfo?.quickFilterId;
    const hasIncrementFromTo =
      report?.reportSections?.hasIncrementFromTo ?? true;

    return this.incrementOptions.getScheduleOptions(
      controlsMap,
      scheduledDateMappings,
      hasFilter,
      hasIncrementFromTo,
    );
  });

  incrementFromOptions = computed(() => {
    const report = this.reports.currentReport();
    const controlsMap = report?.reportRequest?.controlsMap;
    const scheduledDateMappings = report?.reportRequest?.scheduledDateMappings;
    const criteria = this.filterCriteria();
    return this.incrementOptions.getAllOptions(
      'FROM',
      controlsMap,
      scheduledDateMappings,
      criteria,
    );
  });

  incrementToOptions = computed(() => {
    const report = this.reports.currentReport();
    const controlsMap = report?.reportRequest?.controlsMap;
    const scheduledDateMappings = report?.reportRequest?.scheduledDateMappings;
    const criteria = this.filterCriteria();
    return this.incrementOptions.getAllOptions(
      'TO',
      controlsMap,
      scheduledDateMappings,
      criteria,
    );
  });

  fg = new FormGroup({
    frequency: new FormControl<string>('N'),
    runDate: new FormControl<Date | null>(new Date()),
    incDates: new FormControl<string[]>([]),
    incFrom: new FormControl<string[]>([]),
    incTo: new FormControl<string[]>([]),
  });

  // Store original run date for calculating date difference when updating
  private originalRunDate: Date | null = null;

  protected readonly regionName: string = null;

  protected CONTROL_CONFIGS: ControlConfig[] = [];

  private quickFilterId = computed(
    () => this.reports.currentReport()?.reportInfo?.quickFilterId ?? null,
  );

  private initialized = false;

  constructor() {
    super();

    effect(() => {
      this.reports.pendingFilter();
      this.quickFilterId();

      untracked(() => {
        if (this.initialized) {
          this.loadFilterCriteria(true);
        }
      });
    });
  }

  override ngOnInit(): void {
    this.loadSavedValues();
    this.fg.controls.frequency.addValidators([Validators.required]);
    this.frequencyValue.set(this.fg.controls.frequency.value);
    this.updateRunDateValidators(this.fg.controls.frequency.value);
    this.setupFormListeners();
    this.loadFilterCriteria();
    this.initialized = true;
  }

  private async loadFilterCriteria(forceAutoSelect = false): Promise<void> {
    const report = this.reports.currentReport();
    const requiresFilter = report?.reportSections?.requiresFilter ?? false;
    const filterId = report?.reportInfo?.quickFilterId;
    const contractNumber = report?.reportInfo?.contractNumber;

    if (requiresFilter && filterId) {
      const filterDetail = await this.filtersService.getFilterDetail(
        filterId,
        contractNumber ?? null,
      );
      this.filterCriteria.set(filterDetail.criteria);
    } else {
      const pending = this.reports.pendingFilter();
      if (pending?.criteria?.length) {
        this.filterCriteria.set(pending.criteria);
      }
    }

    const frequency = this.fg.controls.frequency.value;
    if (
      !this.reports.isEditScheduledDeliveryMode() &&
      !this.reports.currentReport()?.reportSections?.runOnceOnly &&
      frequency !== 'N' &&
      frequency !== 'P' &&
      (forceAutoSelect ||
        this.reports.isNewMode() ||
        !this.hasExistingIncrementSelections())
    ) {
      this.autoSelectEnabledIncrementOptions();
    }
  }

  private hasExistingIncrementSelections(): boolean {
    const controlsMap =
      this.reports.currentReport()?.reportRequest?.controlsMap;
    if (!controlsMap) return false;

    for (const [erfName, control] of controlsMap) {
      if (erfName.startsWith('INCREMENT_') && control.defaultValue === 'Y') {
        return true;
      }
    }
    return false;
  }

  private autoSelectEnabledIncrementOptions(): void {
    const getEnabledNames = (
      options: { disabled: boolean; displayName: string }[],
    ) => options.filter((o) => !o.disabled).map((o) => o.displayName);

    const fromNames = getEnabledNames(this.incrementFromOptions());
    const toNames = getEnabledNames(this.incrementToOptions());
    const schedNames = getEnabledNames(this.scheduleOptions());

    if (fromNames.length) this.fg.controls.incFrom.setValue(fromNames);
    if (toNames.length) this.fg.controls.incTo.setValue(toNames);
    if (schedNames.length) this.fg.controls.incDates.setValue(schedNames);
  }

  protected override setupFormListeners(): void {
    this.dispose.subs = this.fg.controls.frequency.valueChanges.subscribe(
      (value) => {
        this.frequencyValue.set(value);
        this.updateRunDateValidators(value);
        if (value === 'P' || value === 'N') {
          this.fg.controls.incFrom.setValue([]);
          this.fg.controls.incTo.setValue([]);
          this.fg.controls.incDates.setValue([]);
        } else {
          this.autoSelectEnabledIncrementOptions();
        }
      },
    );

    // Listen for runDate changes to auto-update increment dates
    this.dispose.subs = this.fg.controls.runDate.valueChanges.subscribe(
      (newRunDate) => {
        this.onRunDateChange(newRunDate);
      },
    );
  }

  private updateRunDateValidators(frequency: string | null): void {
    const runDateControl = this.fg.controls.runDate;
    if (frequency && frequency !== 'P') {
      runDateControl.setValidators(Validators.required);
    } else {
      runDateControl.clearValidators();
    }
    runDateControl.updateValueAndValidity();
  }

  private onRunDateChange(newRunDate: Date | null): void {
    // Only update increment dates if:
    // 1. We're in edit-scheduled mode
    // 2. The first run has completed (hasRunOnce is true)
    // 3. We have both original and new run dates
    if (
      !this.reports.isEditScheduledDeliveryMode() ||
      !this.reports.scheduledReportHasRunOnce() ||
      !this.originalRunDate ||
      !newRunDate
    ) {
      return;
    }

    // Calculate the day difference
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysDiff = Math.round(
      (newRunDate.getTime() - this.originalRunDate.getTime()) / msPerDay,
    );

    if (daysDiff === 0) {
      return;
    }

    // Update the scheduled date mappings with the new dates
    this.updateScheduledDateMappings(daysDiff);
  }

  private updateScheduledDateMappings(daysDiff: number): void {
    const report = this.reports.currentReport();
    const scheduledDateMappings = report?.reportRequest?.scheduledDateMappings;

    if (!scheduledDateMappings?.length) {
      return;
    }

    // Update each scheduled date mapping by adding the day difference
    this.reports.currentReport.update((report) => {
      if (!report?.reportRequest?.scheduledDateMappings) {
        return report;
      }

      report.reportRequest.scheduledDateMappings =
        report.reportRequest.scheduledDateMappings.map((mapping) => {
          if (mapping.currentDateValue) {
            const currentDate = new Date(mapping.currentDateValue);
            currentDate.setDate(currentDate.getDate() + daysDiff);
            return {
              ...mapping,
              currentDateValue: currentDate.toISOString(),
            };
          }
          return mapping;
        });

      return report;
    });

    // Update the original run date to the new value for subsequent changes
    this.originalRunDate = this.fg.controls.runDate.value;
  }

  private loadSavedValues(): void {
    const report = this.reports.currentReport();

    if (report.reportRequest?.schedule) {
      const schedule = report.reportRequest.schedule;

      const frequency = schedule.frequency || 'P';

      this.fg.controls.frequency.setValue(frequency);

      const runOnceOnly = report.reportSections?.runOnceOnly ?? false;
      if (runOnceOnly) {
        this.fg.controls.frequency.setValue('N');
      }

      if (
        this.reports.isSchedulePersonalizedMode() &&
        (this.fg.controls.frequency.value === 'P' ||
          !this.fg.controls.frequency.value)
      ) {
        this.fg.controls.frequency.setValue('N');
      }

      if (schedule.dateToRun && frequency !== 'P') {
        const runDate = new Date(schedule.dateToRun);
        this.fg.controls.runDate.setValue(runDate);
        // Store original run date for calculating date difference on changes
        this.originalRunDate = runDate;
      }
    }

    if (report.reportRequest?.controlsMap) {
      const hasIncrementFromTo =
        report.reportSections?.hasIncrementFromTo ?? true;
      this.loadIncrementSelectionsFromControls(
        report.reportRequest.controlsMap,
        hasIncrementFromTo,
      );
    }
  }

  private loadIncrementSelectionsFromControls(
    controlsMap: Map<string, ProcessedControl>,
    hasIncrementFromTo: boolean,
  ): void {
    const incFromFields: string[] = [];
    const incToFields: string[] = [];
    const incDatesFields: string[] = [];

    const erfToDisplayMap =
      this.incrementOptions.getErfToDisplayMapping(hasIncrementFromTo);
    const scheduleOptionErfNames =
      this.incrementOptions.getScheduleOptionErfNames(hasIncrementFromTo);

    controlsMap.forEach((control, erfName) => {
      if (erfName.startsWith('INCREMENT_')) {
        const value = control.defaultValue;
        if (value === 'Y') {
          const displayName = erfToDisplayMap[erfName];
          if (displayName) {
            if (scheduleOptionErfNames.has(erfName)) {
              incDatesFields.push(displayName);
            } else if (erfName.endsWith('_FROM')) {
              incFromFields.push(displayName);
            } else if (erfName.endsWith('_TO')) {
              incToFields.push(displayName);
            }
          }
        }
      }
    });

    if (incFromFields.length > 0) {
      this.fg.controls.incFrom.setValue(incFromFields);
    }
    if (incToFields.length > 0) {
      this.fg.controls.incTo.setValue(incToFields);
    }
    if (incDatesFields.length > 0) {
      this.fg.controls.incDates.setValue(incDatesFields);
    }
  }

  override updateReport(): void {
    this.reports.currentReport.update((report) => {
      if (!report.reportRequest) return report;

      if (report.reportRequest.schedule) {
        const frequencyValue = this.fg.controls.frequency.value || 'P';
        report.reportRequest.schedule.frequency = frequencyValue as
          | 'P'
          | 'N'
          | 'D'
          | 'W'
          | 'M'
          | 'Q'
          | 'S'
          | 'A';

        if (frequencyValue !== 'P') {
          report.reportRequest.schedule.dateToRun =
            this.fg.controls.runDate.value!;
          this.updateIncrementControlsInMap(report.reportRequest.controlsMap);
        } else {
          report.reportRequest.schedule.dateToRun = null;
        }
      }
      return report;
    });
  }

  private updateIncrementControlsInMap(
    controlsMap: Map<string, ProcessedControl> | undefined,
  ): void {
    if (!controlsMap) return;
    const incFromValues = this.fg.controls.incFrom.value || [];
    const incToValues = this.fg.controls.incTo.value || [];
    const incDatesValues = this.fg.controls.incDates.value || [];

    const displayToErfMap = this.incrementOptions.getDisplayToErfMap();

    for (const erfName of this.incrementOptions.getAllIncrementErfNames()) {
      if (controlsMap.has(erfName)) {
        controlsMap.set(erfName, {
          ...controlsMap.get(erfName)!,
          defaultValue: undefined,
        });
      }
    }

    for (const displayName of incFromValues) {
      const erfNames = displayToErfMap[displayName];
      if (erfNames?.from) {
        this.setIncrementControl(controlsMap, erfNames.from);
      }
    }

    for (const displayName of incToValues) {
      const erfNames = displayToErfMap[displayName];
      if (erfNames?.to) {
        this.setIncrementControl(controlsMap, erfNames.to);
      }
    }

    for (const displayName of incDatesValues) {
      const erfNames = displayToErfMap[displayName];
      if (erfNames?.from) {
        this.setIncrementControl(controlsMap, erfNames.from);
      }
    }
  }

  private setIncrementControl(
    controlsMap: Map<string, ProcessedControl>,
    erfName: string,
  ): void {
    const existing = controlsMap.get(erfName);
    controlsMap.set(
      erfName,
      existing
        ? { ...existing, defaultValue: 'Y' }
        : ({
            erfName,
            defaultValue: 'Y',
            region: 'Schedule',
          } as ProcessedControl),
    );
  }
}
